import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';

export interface ClearDataDialogProps {
  open: boolean;
  onClose: () => void;
  /** kind label for the dialog copy ("traces" | "logs"). */
  kind: string;
  /** Selected pair name; null = clear all. */
  pairName: string | null;
  onConfirm: () => Promise<void> | void;
  isLoading?: boolean;
}

/**
 * Type-to-confirm gate for destructive clear actions. The expected text is:
 *   - the pair name when scoped to a single pair
 *   - "CLEAR ALL" when clearing the whole collection
 */
export function ClearDataDialog(props: ClearDataDialogProps) {
  const { open, onClose, kind, pairName, onConfirm, isLoading } = props;
  const expected = pairName ?? 'CLEAR ALL';
  const [typed, setTyped] = useState('');

  const matches = typed === expected;

  const handleClose = () => {
    setTyped('');
    onClose();
  };

  const handleConfirm = async () => {
    if (!matches) return;
    await onConfirm();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Clear {pairName ? `${kind} for "${pairName}"` : `ALL ${kind}`}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          This deletes {pairName ? `every ${kind} entry for this market` : `every ${kind} entry`}.
          Type <strong>{expected}</strong> to confirm.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={expected}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          color="error"
          variant="contained"
          disabled={!matches || isLoading}
          onClick={handleConfirm}
        >
          Clear
        </Button>
      </DialogActions>
    </Dialog>
  );
}
